import { createAsyncThunk, createSelector, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import axios from "axios";
import type { Draft, WritableDraft } from "immer";
import { isDraft, original } from "immer";
import PermissionsAPIService from "permissions/_api/permissions-api-service";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionQuery from "permissions/_api/queries/permission-query";
import PermissionQuerySchema, { PermissionQueryJSON } from "permissions/_api/queries/permission-query-schema";
import Permission from "permissions/permission";
import { PermissionJSON } from "permissions/_schemas/permission-schema";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import { PermissionsByGranteeJSON } from "permissions/by-grantee/_schemas/permissions-by-grantee-schema";
import { SessionPermissions } from "permissions/session/session-permisions";
import Session from "users/__session/session";

type PermissionStoreState = {
  session: {
    entries: PermissionJSON[] | undefined;
    loading: boolean;
    error: string | null;
  };
  byId: {
    entries: Record<string, PermissionJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<PermissionJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byGranteeId: {
    entries: Record<string, PaginatedResponse<PermissionsByGranteeJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, PermissionQueryJSON>;
  };
};

const initialState: PermissionStoreState = {
  session: {
    entries: undefined,
    loading: false,
    error: null,
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  byGranteeId: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

const asPlain = <T>(v: T | Draft<T>): T => (isDraft(v) ? (original(v) as T) : (v as T));

export const populatePermissionsByQuery = createAsyncThunk(
  "permissions/getPermissionsByQuery",
  async ({ session, query }: { session: Session; query: PermissionQuery }, thunkAPI) => {
    const apiService = new PermissionsAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getPermissions(query, abortController);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
);

export const populateSessionPermissions = createAsyncThunk(
  "permissions/populateSessionPermissions",
  async ({ session }: { session: Session }, thunkAPI) => {
    if (!session?.authToken || !session.user?.id) return;
    const apiService = new PermissionsAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);
    const query = new PermissionQuery({
      filterParameters: new PermissionFilterParameters({
        granteeUserIds: [session.user.id],
        hasParent: false,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
    });
    try {
      return await apiService.getPermissions(query, abortController);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
  {
    condition: (_: { session: Session }, { getState }) => {
      const state = getState() as RootState;
      return !state.permissions.session.loading;
    },
  },
);

export const populatePermissionsByGranteeId = createAsyncThunk(
  "permissions/getPermissionsByGranteeId",
  async ({ session, query }: { session: Session; query: PermissionQuery }, thunkAPI) => {
    if (!query) return;
    const apiService = new PermissionsAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getPermissionsByGrantee(query, abortController);
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue("Request canceled");
      }
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
);

export const addPermission = createAsyncThunk<
  PermissionJSON,
  { session: Session; permission: Permission },
  { rejectValue: string }
>("permissions/addPermission", async ({ session, permission }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.addPermission(permission);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const addPermissions = createAsyncThunk<
  PermissionJSON[],
  { session: Session; permissions: Permission[] },
  { rejectValue: string }
>("permissions/addPermissions", async ({ session, permissions }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.bulkAddPermissions(permissions);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const disablePermission = createAsyncThunk<
  PermissionJSON,
  { session: Session; permission: Permission },
  { rejectValue: string }
>("permissions/disablePermission", async ({ session, permission }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.disablePermission(permission);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const enablePermission = createAsyncThunk<
  PermissionJSON,
  { session: Session; permission: Permission },
  { rejectValue: string }
>("permissions/enablePermission", async ({ session, permission }, thunkAPI) => {
  try {
    const api = new PermissionsAPIService(session);
    return await api.enablePermission(permission);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

const resetByQueryReducer = (state: WritableDraft<PermissionStoreState>) => {
  state.byQuery = initialState.byQuery;
};

const resetByGranteeIdReducer = (state: WritableDraft<PermissionStoreState>) => {
  state.byGranteeId = initialState.byGranteeId;
};

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    updatePermissionInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: PermissionQuery;
      }>,
    ) => {
      const { interfaceName, query } = action.payload;

      state.interfaceQueries.entries[interfaceName] = PermissionQuerySchema.serialize(query);
    },
    resetByQuery: resetByQueryReducer,
    resetByGranteeId: resetByGranteeIdReducer,
  },
  extraReducers: (builder) => {
    builder.addCase(populateSessionPermissions.pending, (state) => {
      state.session.loading = true;
      state.session.error = null;
    });
    builder.addCase(populateSessionPermissions.fulfilled, (state, action) => {
      if (!action.payload) return;
      if (action.payload.hasNextPage)
        console.error("populateSessionUserPermissions: more pages exist, but only first page was loaded.");
      state.session.entries = action.payload.data;
      state.session.loading = false;
      state.session.error = null;
    });
    builder.addCase(populateSessionPermissions.rejected, (state, action) => {
      state.session.loading = false;
      state.session.error = action.payload as string;
    });
    builder.addCase(populatePermissionsByQuery.pending, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }
      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = true;
      state.byQuery.error[queryKey] = null;
    });
    builder.addCase(populatePermissionsByQuery.fulfilled, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;

      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.entries[queryKey] = action.payload;
    });
    builder.addCase(populatePermissionsByQuery.rejected, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }
      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.error[queryKey] = action.payload as string;
    });
    builder.addCase(populatePermissionsByGranteeId.pending, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }
      const queryKey = action.meta.arg.query.cacheKey;
      state.byGranteeId.loading[queryKey] = true;
      state.byGranteeId.error[queryKey] = null;
    });
    builder.addCase(populatePermissionsByGranteeId.fulfilled, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }
      const queryKey = action.meta.arg.query.cacheKey;
      state.byGranteeId.loading[queryKey] = false;
      if (action.payload !== undefined) {
        state.byGranteeId.entries[queryKey] = action.payload;
      }
    });
    builder.addCase(populatePermissionsByGranteeId.rejected, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }
      const queryKey = action.meta.arg.query.cacheKey;
      state.byGranteeId.loading[queryKey] = false;
      state.byGranteeId.error[queryKey] = action.payload as string;
    });

    // add.pending - optimistic set
    builder.addCase(addPermission.pending, (state, action) => {
      const permission = action.meta.arg.permission;
      if (permission.id) {
        state.byId.entries[permission.id.value] = action.meta.arg.permission.toJSON();
        state.byId.loading[permission.id.value] = true;
        state.byId.error[permission.id.value] = null;
      }
      // else: no id yet, skip replace; rely on fulfilled below to insert
    });
    // add.fulfilled - server truth
    builder.addCase(addPermission.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = updated?.id ?? action.meta.arg.permission.id?.value;
      if (!id) return;

      state.byId.entries[id] = updated;
      state.byId.loading[id] = false;
      state.byId.error[id] = null;

      resetByQueryReducer(state);
      resetByGranteeIdReducer(state);
    });
    // add.rejected – rollback
    builder.addCase(addPermission.rejected, (state, action) => {
      const orig = action.meta.arg.permission;
      if (!orig?.id) return;

      state.byId.entries[orig.id.value] = orig.toJSON();
      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    // addPermissions.pending – optimistic set
    builder.addCase(addPermissions.pending, (state, action) => {
      for (const perm of action.meta.arg.permissions) {
        const id = perm.id;
        if (id) {
          state.byId.entries[id.value] = perm.toJSON();
          state.byId.loading[id.value] = true;
          state.byId.error[id.value] = null;
        }
      }
    });
    // addPermissions.fulfilled – server truth
    builder.addCase(addPermissions.fulfilled, (state, action) => {
      const updatedPermissions = action.payload;
      for (const updated of updatedPermissions) {
        const id = updated?.id;
        if (!id) continue;

        state.byId.entries[id] = updated;
        state.byId.loading[id] = false;
        state.byId.error[id] = null;
      }

      resetByQueryReducer(state);
      resetByGranteeIdReducer(state);
    });
    // addPermissions.rejected – rollback
    builder.addCase(addPermissions.rejected, (state, action) => {
      for (const perm of action.meta.arg.permissions) {
        const id = perm.id;
        if (id) {
          state.byId.entries[id.value] = perm.toJSON();
          state.byId.loading[id.value] = false;
          state.byId.error[id.value] = action.payload as string;
        }
      }
    });

    // disable.pending – optimistic set
    builder.addCase(disablePermission.pending, (state, action) => {
      const id = action.meta.arg.permission.id;
      if (!id) return;

      const prev = asPlain(state.byId.entries[id.value]);

      if (prev) {
        state.byId.entries[id.value] = {
          ...prev,
          disabledDate: prev.disabledDate ?? prev.createdDate,
        };
      }
      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;
    });
    // disable.fulfilled – server truth
    builder.addCase(disablePermission.fulfilled, (state, action) => {
      const updated = action.payload;
      const permId = updated?.id ?? action.meta.arg.permission.id?.value;
      if (!permId) return;

      state.byId.entries[permId] = updated;
      state.byId.loading[permId] = false;
      state.byId.error[permId] = null;

      resetByQueryReducer(state);
      resetByGranteeIdReducer(state);
    });
    // disable.rejected – rollback
    builder.addCase(disablePermission.rejected, (state, action) => {
      const orig = action.meta.arg.permission;
      if (!orig?.id) return;

      state.byId.entries[orig.id.value] = orig.toJSON();
      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });
    // enable.pending – optimistic
    builder.addCase(enablePermission.pending, (state, action) => {
      const id = action.meta.arg.permission.id;
      if (!id) return;

      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;
    });
    // enable.fulfilled – server truth
    builder.addCase(enablePermission.fulfilled, (state, action) => {
      const updated = action.payload;
      const permId = updated?.id ?? action.meta.arg.permission.id?.value;
      if (!permId) return;

      state.byId.entries[permId] = updated;
      state.byId.loading[permId] = false;
      state.byId.error[permId] = null;

      resetByQueryReducer(state);
      resetByGranteeIdReducer(state);
    });
    // enable.rejected – rollback
    builder.addCase(enablePermission.rejected, (state, action) => {
      const orig = action.meta.arg.permission;
      if (!orig?.id) return;

      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    builder.addMatcher(
      isAnyOf(
        disablePermission.fulfilled,
        disablePermission.rejected,
        enablePermission.fulfilled,
        enablePermission.rejected,
      ),
      (state, action) => {
        const argId = action?.meta?.arg?.permission?.id;
        if (argId) {
          state.byId.loading[argId.value] = false;
        }
      },
    );
  },
});

export const { updatePermissionInterfaceQuery } = permissionsSlice.actions;

const selectSessionPermissions = createSelector(
  [(state: RootState) => state.permissions?.session.entries],
  (entries) => {
    const permissionData = entries?.map((json) => Permission.fromJSON(json));
    return permissionData && new SessionPermissions(permissionData);
  },
);
export const getSessionPermissions = (state: RootState) => selectSessionPermissions(state);
export const getIsLoadingSessionPermissions = (state: RootState) => state.permissions.session.loading;
export const getErrorLoadingSessionPermissions = (state: RootState) => state.permissions.session.error;

const selectPermissionsByQuery = createSelector(
  [(state: RootState) => state.permissions.byQuery.entries, (_: RootState, query?: PermissionQuery) => query?.cacheKey],
  (entries, queryKey) => {
    if (!queryKey) return;
    const pagedJson = entries[queryKey];
    if (!pagedJson) return;
    const permissionData = pagedJson.data.map((json) => Permission.fromJSON(json));
    return PaginatedResponse.from<Permission>(permissionData, pagedJson?.headers);
  },
);
export const getPermissionsByQuery = (query?: PermissionQuery) => (state: RootState) =>
  selectPermissionsByQuery(state, query);
export const getIsLoadingPermissionsByQuery = (query?: PermissionQuery) => (state: RootState) => {
  if (!query) return false;
  return state.permissions.byQuery.loading[query.cacheKey] ?? false;
};
export const getErrorLoadingPermissionsByQuery = (query?: PermissionQuery) => (state: RootState) => {
  if (!query) return false;
  return state.permissions.byQuery.error[query.cacheKey] ?? false;
};

const selectPermissionsByGranteeId = createSelector(
  [
    (state: RootState) => state.permissions.byGranteeId.entries,
    (_: RootState, query?: PermissionQuery) => query?.cacheKey,
  ],
  (entries, queryKey) => {
    if (!queryKey) return;
    const pagedJson = entries[queryKey];
    if (!pagedJson) return;
    const permissionData = PermissionsByGrantee.fromJSONArray(pagedJson.data);
    return PaginatedResponse.from<PermissionsByGrantee>(permissionData, pagedJson?.headers);
  },
);
export const getPermissionsByGranteeId = (query?: PermissionQuery) => (state: RootState) =>
  selectPermissionsByGranteeId(state, query);
export const getIsLoadingPermissionsByGranteeId = (query?: PermissionQuery) => (state: RootState) => {
  if (!query) return false;
  return state.permissions.byGranteeId.loading[query.cacheKey] ?? false;
};
export const getErrorLoadingPermissionsByGranteeId = (query?: PermissionQuery) => (state: RootState) => {
  if (!query) return false;
  return state.permissions.byGranteeId.error[query.cacheKey] ?? false;
};

const selectPermissionById = createSelector(
  [(state: RootState) => state.permissions.byId.entries, (_: RootState, id?: Guid) => id?.value],
  (entries, id) => {
    if (!id) return;
    const entry = entries[id];
    return Permission.fromJSON(entry);
  },
);
export const getPermissionById = (id?: Guid) => (state: RootState) => selectPermissionById(state, id);
export const getIsLoadingPermissionById = (id?: Guid) => (state: RootState) =>
  state.permissions.byId.loading[id?.value ?? ""] ?? false;
export const getErrorLoadingPermissionById = (id?: Guid) => (state: RootState) =>
  state.permissions.byId.error[id?.value ?? ""] ?? false;

const selectPermissionsInterfaceQueryByInterface = createSelector(
  [
    (state: RootState) => state.permissions.interfaceQueries.entries,
    (_: RootState, interfaceName: string) => interfaceName,
  ],
  (entries, interfaceName) => {
    const entry = entries[interfaceName];
    if (!entry) return;
    return PermissionQuerySchema.deserialize(entry);
  },
);
export const getPermissionsQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectPermissionsInterfaceQueryByInterface(state, interfaceName);

export default permissionsSlice;
